const HIGHLIGHT_ATTR = 'data-search-highlight';
const HIGHLIGHT_SELECTOR = `[${HIGHLIGHT_ATTR}]`;

/** Live editors (Tiptap/ProseMirror) and other non-content hosts — injecting <mark> would break editing and risk persisting tags. */
const SKIP_ANCESTOR_SELECTOR = `${HIGHLIGHT_SELECTOR}, script, style, textarea, input, [contenteditable]:not([contenteditable="false"])`;

const MARK_CLASSES = 'bg-warning-400 text-black rounded-xs';
const ACTIVE_MARK_CLASSES = 'bg-warning-600 text-black rounded-xs ring-2 ring-warning-700';

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightTextNode = (textNode: Text, pattern: RegExp, blockId: string | null): void => {
  const text = textNode.textContent ?? '';
  const fragment = document.createDocumentFragment();
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  pattern.lastIndex = 0;
  while ((match = pattern.exec(text))) {
    fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));

    const mark = document.createElement('mark');
    mark.setAttribute(HIGHLIGHT_ATTR, 'true');
    if (blockId) {
      mark.setAttribute('data-block-id', blockId);
    }
    mark.className = MARK_CLASSES;
    mark.textContent = match[0];
    fragment.appendChild(mark);

    lastIndex = match.index + match[0].length;
  }
  fragment.appendChild(document.createTextNode(text.slice(lastIndex)));

  textNode.parentNode?.replaceChild(fragment, textNode);
};

/** The rendered elements holding the content of the given blocks, skipping blocks that are not currently mounted. */
export const getHighlightBlockRoots = (root: ParentNode, blockIds: string[]): Element[] =>
  blockIds.map((blockId) => root.querySelector(`[id="${blockId}"]`)).filter((blockRoot) => blockRoot !== null);

/** Wraps every occurrence of the given terms in a `<mark>` within each rendered block's content. */
export const highlightSearchTerms = (root: ParentNode, terms: string[], blockIds: string[]): void => {
  const cleanTerms = [...new Set(terms.map((term) => term.trim()).filter(Boolean))];
  if (cleanTerms.length === 0) {
    return;
  }

  const pattern = new RegExp(`(${cleanTerms.map(escapeRegExp).join('|')})`, 'gi');

  getHighlightBlockRoots(root, blockIds).forEach((blockRoot) => {
    const blockId = blockRoot.getAttribute('id');
    const walker = document.createTreeWalker(blockRoot, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const parent = node.parentElement;
        if (!parent || parent.closest(SKIP_ANCESTOR_SELECTOR) || parent.isContentEditable) {
          return NodeFilter.FILTER_REJECT;
        }
        pattern.lastIndex = 0;
        return pattern.test(node.textContent ?? '') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
      },
    });

    const textNodes: Text[] = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      textNodes.push(node as Text);
    }

    textNodes.forEach((textNode) => highlightTextNode(textNode, pattern, blockId));
  });
};

/** Removes every highlight `<mark>` previously applied by `highlightSearchTerms`, restoring plain text. */
export const clearSearchHighlights = (root: ParentNode): void => {
  root.querySelectorAll(HIGHLIGHT_SELECTOR).forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) {
      return;
    }
    parent.replaceChild(document.createTextNode(mark.textContent ?? ''), mark);
    parent.normalize();
  });
};

/** Every highlight `<mark>` currently in the document, in visual (document) order. */
export const getSearchHighlightMarks = (root: ParentNode): HTMLElement[] => [
  ...root.querySelectorAll<HTMLElement>(HIGHLIGHT_SELECTOR),
];

/**
 * Marks the highlight at `activeIndex` as the current match (distinct styling) and scrolls it into view.
 * Pass `scroll: false` when re-applying highlights after a block re-render, so the viewport does not jump
 * underneath a user who has scrolled elsewhere.
 */
export const setActiveSearchHighlight = (
  root: ParentNode,
  activeIndex: number,
  onJumpToTarget: (blockId: string) => void,
  { scroll = true }: { scroll?: boolean } = {},
): void => {
  getSearchHighlightMarks(root).forEach((mark, index) => {
    if (index === activeIndex) {
      mark.className = ACTIVE_MARK_CLASSES;
      mark.setAttribute('aria-current', 'true');
      if (scroll) {
        onJumpToTarget(mark.getAttribute('data-block-id') ?? '');
      }
    } else {
      mark.className = MARK_CLASSES;
      mark.removeAttribute('aria-current');
    }
  });
};
